import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { FolderKanban, TrendingUp, Archive, ChevronRight, MoreVertical, Trash2, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { key: 'inquiry', label: 'Inquiry', icon: '❓' },
  { key: 'follow_up', label: 'Follow-up', icon: '📞' },
  { key: 'proposal_sent', label: 'Proposal Sent', icon: '📄' },
  { key: 'contract_signed', label: 'Contract Signed', icon: '✍️' },
  { key: 'initiation', label: 'Initiation', icon: '🎯' },
  { key: 'planning', label: 'Planning', icon: '📋' },
  { key: 'execution', label: 'Execution', icon: '🚀' },
  { key: 'monitoring', label: 'Monitoring + Control', icon: '📊' },
  { key: 'closure', label: 'Closure', icon: '✅' }
];

const Dashboard = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);
  const selectedStageRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    selectedStageRef.current = selectedStage;
  }, [selectedStage]);

  // Format large numbers to compact format (e.g., $160k, $1.5M)
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
    }
    return `$${amount.toLocaleString()}`;
  };

  // Filter out archived and completed projects
  const filterActiveProjects = (projects) => {
    return projects.filter(p => p.status !== 'archived' && p.status !== 'completed');
  };

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/projects`, { headers })
      ]);
      setStats(statsRes.data);
      const activeProjects = filterActiveProjects(projectsRes.data);
      setAllProjects(activeProjects);
      
      // Preserve current filter using ref (to get latest value in interval)
      const currentFilter = selectedStageRef.current;
      if (currentFilter !== null) {
        const filtered = activeProjects.filter(p => p.status === currentFilter);
        setProjects(filtered);
      } else {
        setProjects(activeProjects);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh dashboard every 5 seconds for real-time updates
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 5000);

    return () => clearInterval(refreshInterval);
  }, [fetchData]);

  const handleStageClick = (stageKey) => {
    if (stageKey === null) {
      // "All" clicked - clear filter, show all projects
      setSelectedStage(null);
      setProjects(allProjects);
    } else if (selectedStage === stageKey) {
      // Deselect current stage - show all active projects
      setSelectedStage(null);
      setProjects(allProjects);
    } else {
      // Filter by specific stage
      setSelectedStage(stageKey);
      const filtered = allProjects.filter(p => p.status === stageKey);
      setProjects(filtered);
    }
  };

  const handleMoveToStage = async (projectId, newStatus, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/projects/${projectId}/status`, { status: newStatus }, { headers });
      toast.success(`Project moved to ${PIPELINE_STAGES.find(s => s.key === newStatus)?.label || newStatus}`);
      fetchData();
      // Re-apply filter if a stage is selected
      if (selectedStage) {
        setTimeout(() => {
          setProjects(prev => allProjects.filter(p => p.status === selectedStage));
        }, 500);
      }
    } catch (error) {
      console.error('Failed to update project status:', error);
      toast.error('Failed to move project');
    }
  };

  const handleArchiveProject = async (projectId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to archive this project?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/projects/${projectId}/archive`, {}, { headers });
      toast.success('Project archived successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to archive project:', error);
      toast.error('Failed to archive project');
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this project? It will be moved to Trash and can be recovered from the Archive page.')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/projects/${projectId}/soft-delete`, {}, { headers });
      toast.success('Project moved to Trash');
      fetchData();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleCompleteProject = async (projectId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Mark this project as complete? It will be moved to the Completed section in Archive.')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/projects/${projectId}/complete`, {}, { headers });
      toast.success('Project marked as completed');
      fetchData();
    } catch (error) {
      console.error('Failed to complete project:', error);
      toast.error('Failed to complete project');
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      inquiry: 'status-initiation',
      follow_up: 'status-initiation',
      proposal_sent: 'status-planning',
      contract_signed: 'status-planning',
      initiation: 'status-initiation',
      planning: 'status-planning',
      execution: 'status-execution',
      monitoring: 'status-monitoring',
      closure: 'status-closure',
      active: 'status-active',
      completed: 'status-completed',
      on_hold: 'status-on-hold'
    };
    return statusMap[status] || 'status-inactive';
  };

  const formatStatus = (status) => {
    const statusLabels = {
      inquiry: '❓ Inquiry',
      follow_up: '📞 Follow-up',
      proposal_sent: '📄 Proposal Sent',
      contract_signed: '✍️ Contract Signed',
      initiation: '🎯 Initiation',
      planning: '📋 Planning',
      execution: '🚀 Execution',
      monitoring: '📊 Monitoring',
      closure: '✅ Closure',
      active: '🟢 Active',
      completed: '✨ Completed',
      on_hold: '⏸️ On Hold'
    };
    return statusLabels[status] || status;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
          <div className="text-xl text-slate-gray">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-1 sm:mb-2" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm sm:text-base text-slate-gray">Overview of your business operations</p>
        </div>

        {/* Stats Grid - Admin gets 4 cards, Users get 3 cards */}
        <div className={`grid gap-3 sm:gap-4 lg:gap-6 ${user?.role === 'admin' ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-3'}`}>
          {/* Active Projects Card */}
          <Link to="/dashboard/projects">
            <Card className="shadow-lg rounded-xl border-0 card-hover cursor-pointer h-full" data-testid="stat-card-projects">
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-carbon-black/70 font-semibold mb-1">Active</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-carbon-black">{stats?.total_projects || 0}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FolderKanban className="h-4 w-4 sm:h-6 sm:w-6 text-electric-indigo" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Completed Projects Card */}
          <Link to="/dashboard/archive?tab=completed">
            <Card className="shadow-lg rounded-xl border-0 card-hover cursor-pointer h-full" data-testid="stat-card-completed">
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-carbon-black/70 font-semibold mb-1">Completed</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-carbon-black">{stats?.completed_projects || 0}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Archived Projects Card - Available to all users */}
          <Link to="/dashboard/archive?tab=archived">
            <Card className="shadow-lg rounded-xl border-0 card-hover cursor-pointer h-full" data-testid="stat-card-archived">
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-carbon-black/70 font-semibold mb-1">Archived</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-carbon-black">{stats?.archived_projects || 0}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Archive className="h-4 w-4 sm:h-6 sm:w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Admin-only cards */}
          {user?.role === 'admin' && (
            <>
              {/* Projected Revenue Card */}
              <Card className="shadow-lg rounded-xl border-0 card-hover" data-testid="stat-card-revenue">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-carbon-black/70 font-semibold mb-1">Revenue</p>
                      <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-carbon-black">{formatCurrency(stats?.project_revenue || 0)}</p>
                    </div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 text-indigo-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Project Pipeline - Admin Only */}
        {user?.role === 'admin' && (
          <Card className="shadow-lg rounded-xl border-0">
            <CardHeader className="pb-2 sm:pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-carbon-black">
                  Project Pipeline
                </CardTitle>
                {selectedStage && (
                  <button 
                    onClick={() => handleStageClick(selectedStage)}
                    className="text-sm text-fearless-blue hover:text-electric-indigo transition-colors"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-visible">
                <div className="flex items-stretch min-w-[1100px] py-2 pl-1 pr-2">
                  {/* All Projects Box */}
                  <div className="flex items-center mr-1">
                    <button 
                      onClick={() => handleStageClick(null)}
                      className={`relative flex flex-col items-center justify-center px-4 py-4 rounded-lg min-w-[85px] h-[72px] transition-all cursor-pointer ${
                        selectedStage === null
                          ? 'bg-fearless-blue text-white ring-2 ring-fearless-blue ring-offset-2'
                          : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700'
                      }`}
                      data-testid="pipeline-stage-all"
                    >
                      <span className="text-2xl font-bold text-white">
                        {stats?.total_projects || 0}
                      </span>
                      <span className="text-[10px] text-center mt-1 leading-tight text-gray-200">
                        All
                      </span>
                    </button>
                    <ChevronRight className="h-5 w-5 text-gray-300 mx-1 flex-shrink-0" />
                  </div>
                  {/* Pipeline Stages */}
                  {[
                    { key: 'inquiry', label: 'Inquiry', count: stats?.pipeline_inquiry || 0 },
                    { key: 'follow_up', label: 'Follow-up', count: stats?.pipeline_follow_up || 0 },
                    { key: 'proposal_sent', label: 'Proposal', count: stats?.pipeline_proposal_sent || 0 },
                    { key: 'contract_signed', label: 'Contract', count: stats?.pipeline_contract_signed || 0 },
                    { key: 'initiation', label: 'Initiation', count: stats?.pipeline_initiation || 0 },
                    { key: 'planning', label: 'Planning', count: stats?.pipeline_planning || 0 },
                    { key: 'execution', label: 'Execution', count: stats?.pipeline_execution || 0 },
                    { key: 'monitoring', label: 'Monitoring', count: stats?.pipeline_monitoring || 0 },
                    { key: 'closure', label: 'Closure', count: stats?.pipeline_closure || 0 }
                  ].map((stage, index, arr) => (
                    <div key={stage.key} className={`flex items-center flex-1 ${index === arr.length - 1 ? 'mr-1' : ''}`}>
                      <button 
                        onClick={() => handleStageClick(stage.key)}
                        className={`relative flex flex-col items-center justify-center px-2 py-4 rounded-lg w-full h-[72px] transition-all cursor-pointer ${
                          selectedStage === stage.key
                            ? 'bg-fearless-blue text-white ring-2 ring-fearless-blue ring-offset-2'
                            : stage.count > 0 
                              ? 'bg-carbon-black text-white hover:bg-gray-800' 
                              : 'bg-gray-100 text-slate-gray hover:bg-gray-200'
                        }`}
                        data-testid={`pipeline-stage-${stage.key}`}
                      >
                        <span className={`text-2xl font-bold ${selectedStage === stage.key || stage.count > 0 ? 'text-white' : 'text-carbon-black'}`}>
                          {stage.count}
                        </span>
                        <span className={`text-[10px] text-center mt-1 leading-tight ${selectedStage === stage.key || stage.count > 0 ? 'text-gray-200' : 'text-slate-gray'}`}>
                          {stage.label}
                        </span>
                      </button>
                      {index < arr.length - 1 && (
                        <ChevronRight className="h-5 w-5 text-gray-300 mx-1 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Projects */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-carbon-black" data-testid="recent-projects-title">
              Recent Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <p className="text-slate-gray text-center py-8 text-sm sm:text-base">No projects yet</p>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="relative p-3 sm:p-4 bg-cloud-white rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`project-item-${project.id}`}
                  >
                    <Link
                      to={`/dashboard/projects/${project.id}`}
                      className="block"
                    >
                      {/* Mobile Layout */}
                      <div className="sm:hidden">
                        <div className="flex items-start justify-between mb-2 pr-8">
                          <div className="min-w-0 flex-1">
                            {project.project_number && (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-fearless-blue/10 text-fearless-blue text-[10px] font-mono font-semibold mb-1" data-testid={`dashboard-project-number-mobile-${project.id}`}>
                                {project.project_number}
                              </span>
                            )}
                            <h3 className="font-semibold text-carbon-black text-sm truncate">{project.title}</h3>
                            <p className="text-xs text-slate-gray truncate">{project.client_name}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ml-2 flex-shrink-0 ${getStatusClass(project.status)}`}>
                            {formatStatus(project.status)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between pr-8">
                          <span className="text-sm font-semibold text-carbon-black">
                            ${project.budget.toLocaleString()}
                          </span>
                          <span className="text-xs text-slate-gray">
                            {project.created_at ? new Date(project.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      </div>
                      
                      {/* Desktop Layout */}
                      <div className="hidden sm:flex items-center justify-between pr-10">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {project.project_number && (
                              <span className="inline-block px-2 py-0.5 rounded bg-fearless-blue/10 text-fearless-blue text-xs font-mono font-semibold" data-testid={`dashboard-project-number-desktop-${project.id}`}>
                                {project.project_number}
                              </span>
                            )}
                            <h3 className="font-semibold text-carbon-black truncate">{project.title}</h3>
                          </div>
                          <p className="text-sm text-slate-gray truncate">{project.client_name}</p>
                        </div>
                        <div className="flex items-center space-x-4 flex-shrink-0 ml-4">
                          <span className="text-xs text-slate-gray">
                            {project.created_at ? new Date(project.created_at).toLocaleDateString() : ''}
                          </span>
                          <span className="text-sm font-semibold text-carbon-black">
                            ${project.budget.toLocaleString()}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(project.status)}`}>
                            {formatStatus(project.status)}
                          </span>
                        </div>
                      </div>
                    </Link>

                    {/* 3-dot menu - Admin only */}
                    {user?.role === 'admin' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button 
                              className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                              onClick={(e) => e.preventDefault()}
                            >
                              <MoreVertical className="h-4 w-4 text-slate-gray" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {/* Mobile: Show stages directly in menu */}
                            <div className="sm:hidden">
                              <DropdownMenuLabel className="text-xs text-slate-gray font-medium">
                                Move to Stage
                              </DropdownMenuLabel>
                              <div className="max-h-[200px] overflow-y-auto">
                                {PIPELINE_STAGES.map((stage) => (
                                  <DropdownMenuItem 
                                    key={stage.key}
                                    onClick={(e) => handleMoveToStage(project.id, stage.key, e)}
                                    disabled={project.status === stage.key}
                                    className={project.status === stage.key ? 'bg-gray-100' : ''}
                                  >
                                    <span className="mr-2">{stage.icon}</span>
                                    {stage.label}
                                    {project.status === stage.key && <span className="ml-auto text-xs text-fearless-blue">✓</span>}
                                  </DropdownMenuItem>
                                ))}
                              </div>
                              <DropdownMenuSeparator />
                            </div>
                            {/* Desktop: Use submenu */}
                            <div className="hidden sm:block">
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <ArrowRight className="mr-2 h-4 w-4" />
                                  Move to Stage
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent side="left" className="w-56">
                                  {PIPELINE_STAGES.map((stage) => (
                                    <DropdownMenuItem 
                                      key={stage.key}
                                      onClick={(e) => handleMoveToStage(project.id, stage.key, e)}
                                      disabled={project.status === stage.key}
                                      className={project.status === stage.key ? 'bg-gray-100' : ''}
                                    >
                                      <span className="mr-2">{stage.icon}</span>
                                      {stage.label}
                                      {project.status === stage.key && <span className="ml-auto text-xs text-fearless-blue">✓</span>}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                            </div>
                            <DropdownMenuItem 
                              onClick={(e) => handleCompleteProject(project.id, e)}
                              className="cursor-pointer"
                            >
                              <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                              <span>Completed</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handleArchiveProject(project.id, e)}
                              className="cursor-pointer"
                            >
                              <Archive className="mr-2 h-4 w-4 text-blue-600" />
                              <span>Archive Project</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handleDeleteProject(project.id, e)}
                              className="cursor-pointer text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              <span>Delete Project</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
